/**
 * Ensures API response byte length reaches app code:
 * - Reads X-Response-Body-Bytes (from remote FastAPI middleware or Flask after_request).
 * - For JSON object roots, merges response_body_bytes (original wire length from header when present).
 * - Skips mutating array/primitive JSON roots (body unchanged; header still on the Response until .json()).
 */
import axios from 'axios';

const HEADER_LC = 'x-response-body-bytes';

function parseBytesFromHeaders(getHeader: (name: string) => string | null | undefined): number | undefined {
  const raw = getHeader(HEADER_LC) ?? getHeader('X-Response-Body-Bytes');
  if (raw == null || raw === '') return undefined;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : undefined;
}

function mergeObjectJson(text: string, wireBytes: number | undefined): string {
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
  if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
    const b = wireBytes ?? new TextEncoder().encode(text).length;
    return JSON.stringify({ ...(data as Record<string, unknown>), response_body_bytes: b });
  }
  return text;
}

export function installFetchResponseBodyMeta(): void {
  const g = globalThis as typeof globalThis & { __mmecResponseMetaInstalled?: boolean };
  if (g.__mmecResponseMetaInstalled) return;
  g.__mmecResponseMetaInstalled = true;

  if (typeof window !== 'undefined') {
    const orig = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const res = await orig(input, init);
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        return res;
      }
      const wireBytes = parseBytesFromHeaders((name) => res.headers.get(name));
      const text = await res.text();
      const merged = mergeObjectJson(text, wireBytes);
      const headers = new Headers(res.headers);
      headers.delete('content-length');
      return new Response(merged, {
        status: res.status,
        statusText: res.statusText,
        headers,
      });
    };
  }

  axios.interceptors.response.use((response) => {
    const wireBytes = parseBytesFromHeaders((name) => {
      const h = response.headers;
      if (h && typeof (h as { get?: (n: string) => string | undefined }).get === 'function') {
        const got = (h as { get: (n: string) => string | undefined }).get(name);
        if (got != null) return got;
        return (h as { get: (n: string) => string | undefined }).get(name.toLowerCase());
      }
      return null;
    });
    const d = response.data;
    if (wireBytes != null && d !== null && typeof d === 'object' && !Array.isArray(d)) {
      response.data = { ...d, response_body_bytes: wireBytes };
    }
    return response;
  });
}

installFetchResponseBodyMeta();
